import { DiceRoll } from '@dice-roller/rpg-dice-roller';
import { BranchStep, StepTypes } from '../../../../dorian-shared/types/branch/BranchStep';
import { ChoiceMemory } from '../../../../services/choiceMemory/ChoiceMemory';
import { ChoiceMemoryStack } from '../../../../services/choiceMemory/types/ChoiceMemoryStack';
import { logger } from '../../../../services/loggerService/loggerService';
import { isMemorySlotVariableLink, getMemorySlotVariableLink } from '../../../utils/choiceMemoryUtils';
import {
  CheckOperator, MemoryCheckValueType, MemoryLinkValue, MemoryShowIn, MemoryValue,
} from '../../../pages/Book/MemoryBank/memoryBankTypes';
import { RememberActionType } from '../../../pages/StoryBranches/BranchEdit/Steps/StepTypeRemember';
import { RandomRollType } from '../../../pages/StoryBranches/BranchEdit/Steps/StepTypeRandom';
import { IAchievementGetDTO } from '../../../../dorian-shared/types/achievement/Achievement';
import { buildPlayerHasPrompt } from '../../../utils/playerHasUtils';
import { pushControlledRollbackSnapshot } from './onGoBackStack';

function resolveActionValue(
  actionValue: MemoryValue | MemoryLinkValue | MemoryShowIn[],
  choiceMemory: ChoiceMemory | undefined,
): MemoryValue {
  const value = actionValue as MemoryValue | MemoryLinkValue;
  if (isMemorySlotVariableLink(value)) {
    const link = getMemorySlotVariableLink(value);
    if (link && link.type === MemoryCheckValueType.Variable) {
      const refVariable = choiceMemory?.getVariableById(Number(link.variableId));
      if (refVariable?.value !== undefined) {
        return refVariable.value;
      }
    }
  }
  return value as MemoryValue;
}

export async function onGoForwardStack(
  choiceMemory: ChoiceMemory | undefined,
  episodeId: number,
  step: BranchStep | undefined,
  stepNum: number,
  popupComponent: (text: string, buttonText: string, iconId: number) => Promise<void>,
  // PlayerHas is achievement-based and the emulator has no real ownership state,
  // so the author answers a Yes/No prompt. Optional: when absent (e.g. autonomous
  // player paths) the check routes deterministically to its false branch.
  confirmPlayerHas?: (text: string) => Promise<boolean>,
  achievements: IAchievementGetDTO[] = [],
): Promise<number | 'goAhead' | 'goBack' | undefined> {
  if (step === undefined) {
    return undefined;
  }
  const rollbackVariables = choiceMemory?.currentVariables;

  const item: ChoiceMemoryStack = {
    episodeId,
    step,
    stepNum,
    variables: choiceMemory?.currentVariables ?? {},
  };

  if (step.stepTypeId === StepTypes.Remember) {
    const { action } = item.step;

    switch (action?.type) {
      case RememberActionType.SET.name: {
        const variable = choiceMemory?.getVariableById(action.variableId);
        if (variable !== undefined && variable.value !== undefined && variable.value !== null) {
          const resolvedValue = resolveActionValue(action.value, choiceMemory);
          choiceMemory?.setValueById(
            Number(action.variableId),
            resolvedValue,
          );
          if (variable.type === 'number' && variable.showIn.includes(MemoryShowIn.Alert)) {
            await popupComponent(
              `${variable.displayName} changed!`,
              `Changes: =${resolvedValue}, Total: ${resolvedValue}`,
              variable.icon,
            );
          }
        }
        item.variables = choiceMemory?.currentVariables ?? {};
        break;
      }
      case RememberActionType.INCREASE.name: {
        const variable = choiceMemory?.getVariableById(action.variableId);
        if (variable !== undefined) {
          if (variable.type === 'number') {
            const resolvedValue = resolveActionValue(action.value, choiceMemory);
            const total = Number(variable.value) + Number(resolvedValue);
            choiceMemory?.setValueById(
              action.variableId,
              total,
            );
            if (variable.showIn.includes(MemoryShowIn.Alert)) {
              await popupComponent(
                `${variable.displayName} changed!`,
                `Changes: +${resolvedValue}, Total: ${total}`,
                variable.icon,
              );
            }
          } else {
            throw new Error(`Variable ${variable.name} is not a number, can't use`);
          }
        }
        item.variables = choiceMemory?.currentVariables ?? {};
        break;
      }
      case RememberActionType.DECREASE.name: {
        const variable = choiceMemory?.getVariableById(action.variableId);
        if (variable !== undefined) {
          if (variable.type === 'number') {
            const resolvedValue = resolveActionValue(action.value, choiceMemory);
            const total = Number(variable.value) - Number(resolvedValue);
            choiceMemory?.setValueById(
              action.variableId,
              total,
            );
            if (variable.showIn.includes(MemoryShowIn.Alert)) {
              await popupComponent(
                `${variable.displayName} changed!`,
                `Changes: -${resolvedValue}, Total: ${total}`,
                variable.icon,
              );
            }
          } else {
            throw new Error(`Variable ${variable.name} is not a number, can't use`);
          }
        }
        item.variables = choiceMemory?.currentVariables ?? {};
        break;
      }
      case RememberActionType.VIEW.name: {
        const variable = choiceMemory?.getVariableById(action.variableId);
        if (variable !== undefined) {
          choiceMemory?.setVariableById(variable.id, { ...variable, showIn: action.value as MemoryShowIn[] });
        }
        item.variables = choiceMemory?.currentVariables ?? {};
        break;
      }
      default:
        throw new Error(`Unknown action type ${action?.type}`);
    }

    pushControlledRollbackSnapshot(choiceMemory, rollbackVariables);
    choiceMemory?.push(item);
    return 'goAhead';
  }
  if (step.stepTypeId === StepTypes.Random) {
    const { roll } = item.step;

    // Helper function to generate random value between min and max
    const getRandomValue = (min: MemoryValue, max: MemoryValue): number => {
      const minNum = Number(min);
      const maxNum = Number(max);
      return Math.floor(Math.random() * (maxNum - minNum + 1)) + minNum;
    };


    // Helper function to parse and roll dice notation using the dice roller library
    const rollDiceNotation = (notation: string): number => {
      try {
        const roll = new DiceRoll(notation);
        return roll.total;
      } catch (error) {
        throw new Error(`Invalid dice notation: ${notation}. ${error instanceof Error ? error.message : 'Unknown error'}`);
      }
    };

    switch (roll?.type) {
      case RandomRollType.SET.name: {
        const variable = choiceMemory?.getVariableById(roll.variableId);
        if (variable !== undefined && variable.value !== undefined && variable.value !== null && 'min' in roll && 'max' in roll) {
          const randomValue = getRandomValue(roll.min, roll.max);
          const finalValue = randomValue + Number(roll.modifier || 0);
          choiceMemory?.setValueById(
            Number(roll.variableId),
            finalValue as MemoryValue,
          );
          if (variable.type === 'number' && variable.showIn.includes(MemoryShowIn.Alert)) {
            await popupComponent(
              `${variable.displayName} changed!`,
              `Changes: =${finalValue}, Total: ${finalValue}`,
              variable.icon,
            );
          }
        }
        item.variables = choiceMemory?.currentVariables ?? {};
        break;
      }
      case RandomRollType.INCREASE.name: {
        const variable = choiceMemory?.getVariableById(roll.variableId);
        if (variable !== undefined && 'min' in roll && 'max' in roll) {
          if (variable.type === 'number') {
            const randomValue = getRandomValue(roll.min, roll.max);
            const finalValue = randomValue + Number(roll.modifier || 0);
            const total = Number(variable.value) + finalValue;
            choiceMemory?.setValueById(
              roll.variableId,
              total,
            );
            if (variable.showIn.includes(MemoryShowIn.Alert)) {
              await popupComponent(
                `${variable.displayName} changed!`,
                `Changes: +${finalValue}, Total: ${total}`,
                variable.icon,
              );
            }
          } else {
            throw new Error(`Variable ${variable.name} is not a number, can't use`);
          }
        }
        item.variables = choiceMemory?.currentVariables ?? {};
        break;
      }
      case RandomRollType.DECREASE.name: {
        const variable = choiceMemory?.getVariableById(roll.variableId);
        if (variable !== undefined && 'min' in roll && 'max' in roll) {
          if (variable.type === 'number') {
            const randomValue = getRandomValue(roll.min, roll.max);
            const finalValue = randomValue + Number(roll.modifier || 0);
            const total = Number(variable.value) - finalValue;
            choiceMemory?.setValueById(
              roll.variableId,
              total,
            );
            if (variable.showIn.includes(MemoryShowIn.Alert)) {
              await popupComponent(
                `${variable.displayName} changed!`,
                `Changes: -${finalValue}, Total: ${total}`,
                variable.icon,
              );
            }
          } else {
            throw new Error(`Variable ${variable.name} is not a number, can't use`);
          }
        }
        item.variables = choiceMemory?.currentVariables ?? {};
        break;
      }
      case RandomRollType.DICE.name: {
        const variable = choiceMemory?.getVariableById(roll.variableId);
        if (variable !== undefined && 'diceNotation' in roll) {
          if (variable.type === 'number') {
            try {
              const diceResult = rollDiceNotation(roll.diceNotation);
              choiceMemory?.setValueById(
                Number(roll.variableId),
                diceResult as MemoryValue,
              );
              if (variable.showIn.includes(MemoryShowIn.Alert)) {
                await popupComponent(
                  `${variable.displayName} changed!`,
                  `Rolled: ${roll.diceNotation} = ${diceResult}`,
                  variable.icon,
                );
              }
            } catch (error) {
              // Handle dice notation parsing errors
              console.error('Dice notation error:', error);
              await popupComponent(
                'Dice Roll Error',
                `Invalid dice notation: ${roll.diceNotation}`,
                0, // Use default icon
              );
            }
          } else {
            throw new Error(`Variable ${variable.name} is not a number, can't use dice roll`);
          }
        }
        item.variables = choiceMemory?.currentVariables ?? {};
        break;
      }
      default:
        throw new Error(`Unknown roll type ${roll?.type}`);
    }

    pushControlledRollbackSnapshot(choiceMemory, rollbackVariables);
    choiceMemory?.push(item);
    return 'goAhead';
  }
  if (step.stepTypeId === StepTypes.Check) {
    pushControlledRollbackSnapshot(choiceMemory, rollbackVariables);
    choiceMemory?.push(item);

    const { check, switch: gotoSwitches } = step;

    if (!check) {
      logger.error(`[AT-CM] Check is undefined in stepId=${step.id}`);
      return undefined;
    }

    if (!gotoSwitches) {
      logger.error(`[AT-CM] Switch is undefined in stepId=${step.id}`);
      return undefined;
    }
    if (gotoSwitches.some((gotoSwitch) => !gotoSwitch.gotoBranchId || gotoSwitch.gotoBranchId.toString() === '0')) {
      return undefined;
    }

    // PlayerHas checks own no memory variable (variableId is 0) and cannot be
    // evaluated against memory state — getGotoBranchIdByCheck would throw on the
    // missing variable and the navigation would silently stop. Ask the author
    // instead and route the true/false switch.
    if (CheckOperator[check.operator]?.isPlayerHas) {
      if (!confirmPlayerHas) {
        logger.error(`[AT-CM] PlayerHas check has no confirm handler in stepId=${step.id}; defaulting to false branch`);
      }
      const hasReward = confirmPlayerHas ? await confirmPlayerHas(buildPlayerHasPrompt(check, achievements)) : false;
      const target = gotoSwitches.find((gotoSwitch) => String(gotoSwitch.value) === (hasReward ? 'true' : 'false'));
      if (!target) {
        logger.error(`[AT-CM] PlayerHas check is missing the ${hasReward ? 'true' : 'false'} switch in stepId=${step.id}`);
        return undefined;
      }
      return Number(target.gotoBranchId);
    }

    return choiceMemory?.getGotoBranchIdByCheck(check, gotoSwitches);
  }

  choiceMemory?.push(item);
  return undefined;
}
